import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Users, Plus, Tag, Pencil, Link2, ChevronDown, ChevronRight, MapPin } from 'lucide-react';
import nurseryService from '../../services/nursery';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import type { NurseryCustomer, NurseryCustomerTier } from '../../types/nursery';
import {
  AU_STATES, SORT_LABELS, buildCustomerListView, isCustomerSort, stateCounts, stateHeading,
  type CustomerSort,
} from '../../utils/customerListView';

/** Rows already in state order, split under one heading per state. */
function groupByState(rows: NurseryCustomer[]): [string, NurseryCustomer[]][] {
  const groups: [string, NurseryCustomer[]][] = [];
  for (const c of rows) {
    const heading = stateHeading(c.state);
    const last = groups[groups.length - 1];
    if (last && last[0] === heading) last[1].push(c); else groups.push([heading, [c]]);
  }
  return groups;
}

function CustomerList() {
  const navigate = useNavigate();
  const { hasAccess: canManage } = useModuleLicense('accounts', ['user', 'admin']);

  const [customers, setCustomers] = useState<NurseryCustomer[]>([]);
  const [tiers, setTiers] = useState<NurseryCustomerTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // Sort and state live in the URL, so Back from a customer keeps them.
  const [params, setParams] = useSearchParams();
  const sortParam = params.get('sort');
  const sort: CustomerSort = isCustomerSort(sortParam) ? sortParam : 'name';
  const stateFilter = params.get('state') ?? '';
  const setParam = (key: string, value: string, fallback: string) => setParams(prev => {
    const next = new URLSearchParams(prev);
    if (value === fallback) next.delete(key); else next.set(key, value);
    return next;
  }, { replace: true });
  // Head offices whose sites are showing. Collapsed to start with; a search
  // opens them all, since the match may be a site.
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  // Inline tier management
  const [showTiers, setShowTiers] = useState(false);
  const [newTierName, setNewTierName] = useState('');
  const [newTierDiscount, setNewTierDiscount] = useState('');
  const [newTierRequiresApproval, setNewTierRequiresApproval] = useState(false);
  const [newTierPaymentMode, setNewTierPaymentMode] = useState<'online' | 'on_account'>('on_account');
  const [savingTier, setSavingTier] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [custRes, tierRes] = await Promise.all([
        nurseryService.getCustomers({ active: true, ...(search ? { search } : {}) }),
        nurseryService.getCustomerTiers(),
      ]);
      if (custRes.success) setCustomers(custRes.data || []);
      if (tierRes.success) setTiers(tierRes.data || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const { topLevel, sitesOf, shown } = useMemo(
    () => buildCustomerListView(customers, sort, stateFilter),
    [customers, sort, stateFilter],
  );
  const counts = useMemo(() => stateCounts(customers), [customers]);
  // States with customers, in the usual order; "not known" last.
  const stateOptions = AU_STATES.filter(st => counts.has(st));
  const unknownCount = counts.get('none') ?? 0;

  // A search or a state shows the matching sites, open.
  const searching = search.trim() !== '' || stateFilter !== '';
  const toggle = (id: number) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const renderRow = (c: NurseryCustomer, indented: boolean) => {
    const sites = sitesOf.get(c.id) ?? [];
    const siteCount = Number(c.site_count ?? sites.length) || 0;
    const open = searching || expanded.has(c.id);
    const orphanSite = !indented && Boolean(c.parent_customer_id);
    const place = [c.suburb, c.state].filter(Boolean).join(' ');
    return (
      <div key={c.id}>
        <div className={`flex items-start gap-3 px-4 py-3 hover:bg-neutral-50 ${indented ? 'pl-8 sm:pl-12 bg-neutral-50/50' : ''}`}>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <Link to={`/accounts/customers/${c.id}`} className="font-medium text-neutral-800 hover:text-primary-700 hover:underline break-words min-w-0">
                {c.name}
              </Link>
              {c.customer_code && <span className="text-xs text-neutral-500 break-words min-w-0">#{c.customer_code}</span>}
              {c.tier_name && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-indigo-50 text-indigo-700 border border-indigo-100 break-words min-w-0">
                  {c.tier_name}
                </span>
              )}
              {place && (
                <span className="inline-flex items-center gap-1 text-xs text-neutral-500 break-words min-w-0">
                  <MapPin className="h-3 w-3 shrink-0" /> {place}
                </span>
              )}
              {c.linked_org_id && (
                <span className="inline-flex items-center gap-1 text-xs text-neutral-400" title={c.linked_org_name || 'Linked organisation'}>
                  <Link2 className="h-3 w-3" /> linked
                </span>
              )}
            </div>
            {orphanSite && (
              <p className="text-xs text-neutral-500 break-words">
                Site of{' '}
                <Link to={`/accounts/customers/${c.parent_customer_id}`} className="text-primary-600 hover:underline">
                  {c.parent_name || 'its head office'}
                </Link>
              </p>
            )}
            <p className="text-xs text-neutral-500 break-words">
              {[c.contact_name, c.email, c.phone].filter(Boolean).join(' · ') || 'No contact details'}
            </p>
            {siteCount > 0 && (sites.length > 0 ? (
              <button
                type="button"
                onClick={() => toggle(c.id)}
                aria-expanded={open}
                disabled={searching}
                className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline disabled:no-underline disabled:text-neutral-600"
              >
                {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                {siteCount} site{siteCount === 1 ? '' : 's'}
                {searching && sites.length < siteCount ? ` (${sites.length} shown)` : ''}
              </button>
            ) : (
              <p className="mt-1 text-xs text-neutral-500">{siteCount} site{siteCount === 1 ? '' : 's'}</p>
            ))}
          </div>
          {canManage && (
            <button
              onClick={() => navigate(`/accounts/customers/${c.id}/edit`)}
              className="text-neutral-400 hover:text-primary-600 p-1 shrink-0"
              title="Edit"
              aria-label={`Edit ${c.name}`}
            >
              <Pencil className="h-4 w-4" />
            </button>
          )}
        </div>
        {open && sites.length > 0 && (
          <div className="divide-y divide-neutral-100 border-t border-neutral-100">
            {sites.map(site => renderRow(site, true))}
          </div>
        )}
      </div>
    );
  };

  const refreshTiers = async () => {
    const tierRes = await nurseryService.getCustomerTiers();
    if (tierRes.success) setTiers(tierRes.data || []);
  };

  const addTier = async () => {
    if (!newTierName.trim()) return;
    setSavingTier(true);
    try {
      const res = await nurseryService.createCustomerTier({
        name: newTierName.trim(),
        default_discount_pct: newTierDiscount ? Number(newTierDiscount) : 0,
        requires_approval: newTierRequiresApproval,
        payment_mode: newTierPaymentMode,
      });
      if (res.success) {
        setNewTierName('');
        setNewTierDiscount('');
        setNewTierRequiresApproval(false);
        setNewTierPaymentMode('on_account');
        await refreshTiers();
      }
    } finally {
      setSavingTier(false);
    }
  };

  const patchTier = async (id: number, data: Partial<NurseryCustomerTier>) => {
    const res = await nurseryService.updateCustomerTier(id, data);
    if (res.success) await refreshTiers();
  };

  return (
    <>
      <PageHeader
        title="Customers"
        subtitle="Customer registry, pricing tiers and contacts"
        icon={<Users className="h-6 w-6 text-white" />}
        actionButtons={
          canManage && (
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowTiers(s => !s)}>
                <Tag className="h-4 w-4" /> Tiers
              </Button>
              <Button variant="primary" size="sm" onClick={() => navigate('/accounts/customers/new')}>
                <Plus className="h-4 w-4" /> New Customer
              </Button>
            </div>
          )
        }
      />
      <div className="max-w-7xl mx-auto px-4 py-6">
        {showTiers && (
          <div className="bg-white border border-neutral-200 rounded-xl p-5 mb-6">
            <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide mb-1">Pricing Tiers</h3>
            <p className="text-xs text-neutral-400 mb-4">
              Tiers scope price lists and default discounts. The <strong>website default</strong> tier is what
              self-signup (retail) customers get; tiers marked <strong>requires approval</strong> are wholesale
              tiers customers apply for. <strong>Payment mode</strong> decides whether the customer pays online
              at checkout or is invoiced on account.
            </p>

            {tiers.length === 0 ? (
              <p className="text-sm text-neutral-400 mb-4">No tiers yet.</p>
            ) : (
              <div className="border border-neutral-200 rounded-lg divide-y divide-neutral-100 mb-4">
                {tiers.map(t => (
                  <div key={t.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2.5 text-sm">
                    <span className="font-medium text-neutral-800 min-w-[8rem]">
                      {t.name}{Number(t.default_discount_pct) > 0 ? ` · ${t.default_discount_pct}%` : ''}
                    </span>
                    {canManage ? (
                      <>
                        <label className="inline-flex items-center gap-1.5 text-xs text-neutral-600">
                          <input
                            type="radio"
                            name="website-default-tier"
                            checked={t.is_website_default}
                            onChange={() => patchTier(t.id, { is_website_default: true })}
                          />
                          Website default
                        </label>
                        <label className="inline-flex items-center gap-1.5 text-xs text-neutral-600">
                          <input
                            type="checkbox"
                            checked={t.requires_approval}
                            onChange={e => patchTier(t.id, { requires_approval: e.target.checked })}
                          />
                          Requires approval
                        </label>
                        <select
                          value={t.payment_mode}
                          onChange={e => patchTier(t.id, { payment_mode: e.target.value as 'online' | 'on_account' })}
                          className="text-xs border border-neutral-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500"
                        >
                          <option value="online">Pays online</option>
                          <option value="on_account">On account</option>
                        </select>
                      </>
                    ) : (
                      <span className="text-xs text-neutral-500">
                        {t.is_website_default ? 'Website default · ' : ''}
                        {t.requires_approval ? 'Requires approval · ' : ''}
                        {t.payment_mode === 'online' ? 'Pays online' : 'On account'}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {canManage && (
              <div className="flex flex-wrap items-end gap-2">
                <div className="flex-1 min-w-[10rem]">
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Tier name</label>
                  <input
                    value={newTierName}
                    onChange={e => setNewTierName(e.target.value)}
                    placeholder="e.g. Garden Centre"
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div className="w-28">
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Default disc. %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={newTierDiscount}
                    onChange={e => setNewTierDiscount(e.target.value)}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div className="w-36">
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Payment</label>
                  <select
                    value={newTierPaymentMode}
                    onChange={e => setNewTierPaymentMode(e.target.value as 'online' | 'on_account')}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="online">Pays online</option>
                    <option value="on_account">On account</option>
                  </select>
                </div>
                <label className="inline-flex items-center gap-1.5 text-xs text-neutral-600 pb-2.5">
                  <input
                    type="checkbox"
                    checked={newTierRequiresApproval}
                    onChange={e => setNewTierRequiresApproval(e.target.checked)}
                  />
                  Requires approval
                </label>
                <Button variant="outline" size="sm" onClick={addTier} disabled={savingTier || !newTierName.trim()}>
                  <Plus className="h-4 w-4" /> Add
                </Button>
              </div>
            )}
          </div>
        )}

        <div className="mb-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              aria-label="Search customers"
              placeholder="Search name, suburb, street, phone, email…"
              className="w-full sm:w-auto sm:flex-1 sm:max-w-sm min-w-0 border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <label className="inline-flex items-center gap-2 text-sm text-neutral-600">
              Sort
              <select
                value={sort}
                onChange={e => setParam('sort', e.target.value, 'name')}
                className="border border-neutral-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {(Object.keys(SORT_LABELS) as CustomerSort[]).map(k => (
                  <option key={k} value={k}>{SORT_LABELS[k]}</option>
                ))}
              </select>
            </label>
          </div>
          {(stateOptions.length > 0 || stateFilter) && (
            <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter by state">
              {[
                { key: '', label: 'All states', count: customers.length },
                ...stateOptions.map(st => ({ key: st, label: st, count: counts.get(st) ?? 0 })),
                ...(unknownCount > 0 ? [{ key: 'none', label: 'Not known', count: unknownCount }] : []),
              ].map(opt => {
                const active = stateFilter === opt.key;
                return (
                  <button
                    key={opt.key || 'all'}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setParam('state', opt.key, '')}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border ${
                      active
                        ? 'bg-primary-600 border-primary-600 text-white'
                        : 'bg-white border-neutral-300 text-neutral-700 hover:bg-neutral-50'
                    }`}
                  >
                    {opt.label} <span className={active ? 'text-white' : 'text-neutral-600'}>{opt.count}</span>
                  </button>
                );
              })}
            </div>
          )}
          {!loading && customers.length > 0 && (
            <p className="text-xs text-neutral-500">
              {shown === customers.length
                ? `${customers.length} customer${customers.length === 1 ? '' : 's'}`
                : `Showing ${shown} of ${customers.length} customers`}
            </p>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 mb-6">{error}</div>
        )}

        {loading ? (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-neutral-100 rounded-lg animate-pulse" />)}
          </div>
        ) : topLevel.length === 0 ? (
          <div className="text-center py-16 text-neutral-400">
            <Users className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium text-neutral-600">No customers found</p>
            {canManage && (
              <Button variant="primary" className="mt-4" onClick={() => navigate('/accounts/customers/new')}>
                <Plus className="h-4 w-4" /> New Customer
              </Button>
            )}
          </div>
        ) : (
          sort === 'state' ? (
            <div className="space-y-4">
              {groupByState(topLevel).map(([heading, rows]) => (
                <section key={heading}>
                  <h2 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-1.5 px-1">
                    {heading} <span className="font-normal normal-case">· {rows.length}</span>
                  </h2>
                  <div className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100 overflow-hidden">
                    {rows.map(c => renderRow(c, false))}
                  </div>
                </section>
              ))}
            </div>
          ) : (
            <div className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100 overflow-hidden">
              {topLevel.map(c => renderRow(c, false))}
            </div>
          )
        )}
      </div>
    </>
  );
}

export default CustomerList;
